import React, { Fragment, useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import Footer from '../../components/footer';
import Navbar from '../../components/Navbar';
import Scrollbar from '../../components/scrollbar';
// import Logo from '../../images/Logo/logo-big.png';
import Logo from '../../images/Logo/logo-white-bg.png';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import CustomLoader from '../../components/CustomLoader';
import defaultImage from '../../images/barrel.png';
import productVideo from '../../assets/videos/product_short.mp4';
import './style.scss';

const PRODUCTS_PER_PAGE = 10;
const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

const ProductList = () => {
    const [products, setProducts] = useState([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [sortOrder, setSortOrder] = useState('asc');
    const [currentPage, setCurrentPage] = useState(1);
    const [activeLetter, setActiveLetter] = useState('');
    const navigate = useNavigate();
    const gridRef = useRef(null);

    const getProduct = async () => {
        try {
            setLoading(true);
            const response = await axios.get(process.env.REACT_APP_API_BASE_URL + '/products');
            const sorted = [...response.data].sort((a, b) => a.name.localeCompare(b.name));
            setProducts(sorted);
            setLoading(false);
        } catch (error) {
            setLoading(false);
            console.error('Error fetching data:', error);
        }
    };

    useEffect(() => {
        window.scrollTo(0, 0);
        getProduct();
    }, []);

    const handleImageError = (e) => {
        e.target.src = defaultImage;
    };

    const handleSortChange = (value) => {
        setSortOrder(value);
        setCurrentPage(1);
    };

    const handleSearchChange = (e) => {
        setSearch(e.target.value);
        setActiveLetter('');
        setCurrentPage(1);
    };

    const handleLetterClick = (letter) => {
        const next = activeLetter === letter ? '' : letter;
        setActiveLetter(next);
        setSearch('');
        setCurrentPage(1);
    };

    // Which letters actually have products
    const availableLetters = new Set(
        products.map(p => p.name.charAt(0).toUpperCase())
    );

    const filteredProducts = products
        .filter(p => {
            const matchesSearch = p.name.toLowerCase().includes(search.toLowerCase());
            const matchesLetter = activeLetter
                ? p.name.charAt(0).toUpperCase() === activeLetter
                : true;
            return matchesSearch && matchesLetter;
        })
        .sort((a, b) =>
            sortOrder === 'asc'
                ? a.name.localeCompare(b.name)
                : b.name.localeCompare(a.name)
        );

    const totalPages = Math.ceil(filteredProducts.length / PRODUCTS_PER_PAGE);
    const paginatedProducts = filteredProducts.slice(
        (currentPage - 1) * PRODUCTS_PER_PAGE,
        currentPage * PRODUCTS_PER_PAGE
    );

    const handlePageChange = (page) => {
        setCurrentPage(page);
        if (gridRef.current) {
            gridRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const getPaginationPages = () => {
        const pages = [];
        const delta = 2;
        for (let i = Math.max(1, currentPage - delta); i <= Math.min(totalPages, currentPage + delta); i++) {
            pages.push(i);
        }
        return pages;
    };

    return (
        <Fragment>
            <Helmet>
                <meta charSet="utf-8" />
                <title>Products - Deep Prabha Transchem</title>
                <link rel="canonical" href="https://www.deepprabha.com/products" />
                <meta name="description" content="Browse Deep Prabha Transchem's extensive range of Solvents, Petrochemicals, Pharmaceutical & Industrial Chemicals. Premium quality, competitive pricing, pan-India delivery." />
                <meta name="keywords" content="buy chemicals india, solvent supplier india, petrochemicals distributor, pharmaceutical chemicals supplier, acetone supplier, ethanol supplier, IPA supplier, toluene supplier, xylene supplier, ethyl acetate supplier, methanol supplier, industrial chemicals india, specialty chemicals supplier india, bulk chemical order india" />
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://www.deepprabha.com/products" />
                <meta property="og:title" content="Chemical Products — Deep Prabha Transchem" />
                <meta property="og:description" content="Explore our comprehensive range of solvents, petrochemicals and pharmaceutical products. Trusted by industries across 85+ countries." />
                <meta property="og:image" content="https://www.deepprabha.com/logo512.png" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Chemical Products — Deep Prabha Transchem" />
                <meta name="twitter:image" content="https://www.deepprabha.com/logo512.png" />
            </Helmet>

            <CustomLoader show={loading} />
            <Navbar topbarBlock={'wpo-header-style-3'} hclass={'wpo-header-style-3'} Logo={Logo} />

            <section className="dp-products-hero">
                <video
                    className="dp-products-hero__video"
                    src={productVideo}
                    autoPlay
                    muted
                    loop
                    playsInline
                />
                <div className="dp-products-hero__overlay" />
                <div className="dp-products-hero__content">
                    <span className="dp-products-hero__label">Our Portfolio</span>
                    <h1 className="dp-products-hero__title">
                        Premium Chemical<br />Products
                    </h1>
                    <p className="dp-products-hero__sub">
                        Trusted by industries worldwide — solvents, acids, amines, reagents &amp; more.
                    </p>
                    <div className="dp-products-hero__stats">
                        <div className="dp-products-hero__stat">
                            <span className="dp-products-hero__stat-num">100+</span>
                            <span className="dp-products-hero__stat-label">Products</span>
                        </div>
                        <div className="dp-products-hero__stat-divider" />
                        <div className="dp-products-hero__stat">
                            <span className="dp-products-hero__stat-num">10+</span>
                            <span className="dp-products-hero__stat-label">Industries Served</span>
                        </div>
                        <div className="dp-products-hero__stat-divider" />
                        <div className="dp-products-hero__stat">
                            <span className="dp-products-hero__stat-num">ISO</span>
                            <span className="dp-products-hero__stat-label">Certified Quality</span>
                        </div>
                    </div>
                </div>
                <div className="dp-products-hero__scroll"><span /></div>
            </section>

            <section className="dp-products-section" ref={gridRef}>
                <div className="dp-products-section__inner">

                    {/* Controls */}
                    <div className="dp-products-controls">
                        <div className="dp-products-controls__search">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
                            </svg>
                            <input
                                type="text"
                                placeholder="Search products..."
                                value={search}
                                onChange={handleSearchChange}
                            />
                            {search && (
                                <button className="dp-products-controls__clear" onClick={() => { setSearch(''); setCurrentPage(1); }}>
                                    x
                                </button>
                            )}
                        </div>
                        <div className="dp-products-controls__sort">
                            <button
                                className={sortOrder === 'asc' ? 'active' : ''}
                                onClick={() => handleSortChange('asc')}
                            >
                                A to Z
                            </button>
                            <button
                                className={sortOrder === 'desc' ? 'active' : ''}
                                onClick={() => handleSortChange('desc')}
                            >
                                Z to A
                            </button>
                        </div>
                        <div className="dp-products-controls__count">
                            {filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''} found
                        </div>
                    </div>

                    {/* Alphabet Filter */}
                    <div className="dp-alphabet-filter">
                        <button
                            className={`dp-alphabet-filter__btn dp-alphabet-filter__btn--all${activeLetter === '' ? ' active' : ''}`}
                            onClick={() => { setActiveLetter(''); setSearch(''); setCurrentPage(1); }}
                        >
                            All
                        </button>
                        {ALPHABET.map(letter => (
                            <button
                                key={letter}
                                className={`dp-alphabet-filter__btn${activeLetter === letter ? ' active' : ''}${!availableLetters.has(letter) ? ' disabled' : ''}`}
                                onClick={() => availableLetters.has(letter) && handleLetterClick(letter)}
                                disabled={!availableLetters.has(letter)}
                                aria-label={`Filter by ${letter}`}
                            >
                                {letter}
                            </button>
                        ))}
                    </div>

                    {/* Active filter badge */}
                    {activeLetter && (
                        <div className="dp-alphabet-filter__active-tag">
                            Showing products starting with
                            <strong> "{activeLetter}"</strong>
                            <button onClick={() => { setActiveLetter(''); setCurrentPage(1); }}>
                                ✕ Clear
                            </button>
                        </div>
                    )}

                    {/* Grid */}
                    {paginatedProducts.length === 0 && !loading ? (
                        <div className="dp-products-empty">
                            <span>No products match your search.</span>
                            <button onClick={() => { setSearch(''); setActiveLetter(''); }}>Clear filters</button>
                        </div>
                    ) : (
                        <div className="dp-products-grid">
                            {paginatedProducts.map((product, idx) => (
                                <div
                                    key={product._id}
                                    className="dp-product-card"
                                    style={{ animationDelay: `${idx * 0.05}s` }}
                                    onClick={() => navigate(`/products/${product._id}`)}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => e.key === 'Enter' && navigate(`/products/${product._id}`)}
                                >
                                    <div className="dp-product-card__img-wrap">
                                        <img
                                            src={product.image || defaultImage}
                                            alt={product.name}
                                            onError={handleImageError}
                                            loading="lazy"
                                        />
                                        <div className="dp-product-card__img-overlay">
                                            <span>View Details</span>
                                        </div>
                                    </div>
                                    <div className="dp-product-card__body">
                                        <h3 className="dp-product-card__name">{product.name}</h3>
                                        <p className="dp-product-card__desc">
                                            {product.shortDescription || 'High-quality chemical product for industrial use.'}
                                        </p>
                                        <div className="dp-product-card__footer">
                                            <button className="dp-product-card__btn">
                                                View Details
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="dp-products-pagination">
                            <button
                                className="dp-products-pagination__btn"
                                disabled={currentPage === 1}
                                onClick={() => handlePageChange(currentPage - 1)}
                            >
                                Prev
                            </button>

                            {currentPage > 3 && (
                                <>
                                    <button className="dp-products-pagination__btn" onClick={() => handlePageChange(1)}>1</button>
                                    {currentPage > 4 && <span className="dp-products-pagination__ellipsis">...</span>}
                                </>
                            )}

                            {getPaginationPages().map(page => (
                                <button
                                    key={page}
                                    className={currentPage === page ? 'dp-products-pagination__btn active' : 'dp-products-pagination__btn'}
                                    onClick={() => handlePageChange(page)}
                                >
                                    {page}
                                </button>
                            ))}

                            {currentPage < totalPages - 2 && (
                                <>
                                    {currentPage < totalPages - 3 && <span className="dp-products-pagination__ellipsis">...</span>}
                                    <button className="dp-products-pagination__btn" onClick={() => handlePageChange(totalPages)}>{totalPages}</button>
                                </>
                            )}

                            <button
                                className="dp-products-pagination__btn"
                                disabled={currentPage === totalPages}
                                onClick={() => handlePageChange(currentPage + 1)}
                            >
                                Next
                            </button>

                            <span className="dp-products-pagination__info">
                                Page {currentPage} of {totalPages}
                            </span>
                        </div>
                    )}
                </div>
            </section>

            <Footer />
            <Scrollbar />
        </Fragment>
    );
};

export default ProductList;
